import { supabase } from '../lib/supabase';

export type NotificationType =
  | 'task_assigned'
  | 'task_completed'
  | 'task_overdue'
  | 'task_due_soon'
  | 'timer_running_long'
  | 'project_hours_budget'
  | 'task_status_changed'
  | 'task_commented'
  | 'file_uploaded'
  | 'file_updated'
  | 'file_shared'
  | 'document_approval_requested'
  | 'project_member_added'
  | 'project_member_removed'
  | 'role_changed'
  | 'access_requested'
  | 'access_granted'
  | 'meeting_scheduled'
  | 'integration_event'
  | 'system_error'
  | 'chat_mention'
  | 'chat_new_message'
  | 'chat_member_added'
  | 'chat_member_removed';

export interface CreateNotificationParams {
  organization_id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  content: string;
  metadata?: Record<string, any>;
}

export async function createNotification({
  organization_id,
  user_id,
  type,
  title,
  content,
  metadata = {},
}: CreateNotificationParams) {
  const { data, error } = await supabase
    .from('notifications')
    .insert([
      {
        organization_id,
        user_id,
        type,
        title,
        content,
        metadata,
      },
    ]);
  if (error) throw error;
  return data;
}

// Para onde o clique numa notificação leva. null = notificação sem destino.
// - Atividade do Kanban (metadata.source = 'kanban'): abre o quadro do projeto
//   com a atividade aberta (SimpleKanbanBoard lê ?project= e ?task=).
// - Nota pessoal (simple_task): abre Minhas notas com o detalhe aberto
//   (UnifiedTaskView lê o ?task=).
// - Resumo de prazos: abre Minhas atividades.
// - Cronômetro esquecido: abre Uso do tempo, onde dá pra parar o timer.
// - Projeto estourando horas: abre a página do projeto (a rota é o id).
// - Chat: não é rota — o Header abre a sala no popup (ver getNotificationChatRoom).
const TIPOS_DE_ATIVIDADE = ['task_assigned', 'task_due_soon', 'task_overdue'];

export function getNotificationLink(notification: {
  type?: string;
  metadata?: Record<string, unknown> | null;
}): string | null {
  if (notification.type === 'timer_running_long') return '/time';
  if (notification.type === 'project_hours_budget') {
    const projectId = notification.metadata?.projectId;
    return typeof projectId === 'string' && projectId ? `/${encodeURIComponent(projectId)}` : null;
  }
  if (!notification.type || !TIPOS_DE_ATIVIDADE.includes(notification.type)) return null;
  const { taskId, projectId, source } = notification.metadata ?? {};
  const temTarefa = typeof taskId === 'string' && taskId !== '';

  if (source === 'kanban-summary') return '/my-activities';
  if (source === 'kanban' && typeof projectId === 'string' && projectId) {
    const params = new URLSearchParams({ project: projectId });
    if (temTarefa) params.set('task', taskId as string);
    return `/activities?${params.toString()}`;
  }
  if (notification.type === 'task_assigned' && temTarefa) {
    return `/tasks-unified?task=${encodeURIComponent(taskId as string)}`;
  }
  return null;
}

// Sala do chat que a notificação abre, ou null.
export function getNotificationChatRoom(notification: {
  type?: string;
  metadata?: Record<string, unknown> | null;
}): string | null {
  if (notification.type !== 'chat_mention' && notification.type !== 'chat_new_message') return null;
  const roomId = notification.metadata?.roomId;
  return typeof roomId === 'string' && roomId ? roomId : null;
}

// Quem recebe uma atribuição de atividade. Ninguém se notifica, e trocar
// outros campos sem mexer no responsável não renotifica.
export function activityAssignmentRecipient(
  actorId: string | null | undefined,
  assigneeId: string | null | undefined,
  previousAssigneeId?: string | null
): string | null {
  if (!assigneeId) return null;
  if (assigneeId === actorId) return null;
  if (assigneeId === previousAssigneeId) return null;
  return assigneeId;
}

// Avisa o responsável de uma ou mais atividades do Kanban do mesmo projeto.
// Várias de uma vez (aplicar template) viram um aviso só. Nunca lança: falhar
// a notificação não pode desfazer o salvamento da atividade.
export async function notifyActivityAssigned({
  organizationId,
  actorId,
  assigneeId,
  previousAssigneeId,
  projectId,
  tasks,
}: {
  organizationId: string;
  actorId: string | null | undefined;
  assigneeId: string | null | undefined;
  previousAssigneeId?: string | null;
  projectId: string | null | undefined;
  tasks: Array<{ id?: string; title: string }>;
}): Promise<void> {
  const destinatario = activityAssignmentRecipient(actorId, assigneeId, previousAssigneeId);
  if (!destinatario || !projectId || tasks.length === 0) return;

  try {
    const [{ data: autor }, { data: projeto }] = await Promise.all([
      supabase.from('user_profile').select('name, email').eq('auth_id', actorId ?? '').maybeSingle(),
      supabase.from('projects').select('name').eq('id', projectId).maybeSingle(),
    ]);
    const nomeAutor = (autor as { name?: string | null; email?: string | null } | null)?.name
      || (autor as { email?: string | null } | null)?.email
      || 'Alguém';
    const nomeProjeto = (projeto as { name?: string | null } | null)?.name || 'sem nome';

    const uma = tasks.length === 1;
    await createNotification({
      organization_id: organizationId,
      user_id: destinatario,
      type: 'task_assigned',
      title: uma ? 'Nova atividade atribuída' : `${tasks.length} novas atividades atribuídas`,
      content: uma
        ? `${nomeAutor} atribuiu a você a atividade "${tasks[0].title}" no projeto "${nomeProjeto}".`
        : `${nomeAutor} atribuiu a você ${tasks.length} atividades no projeto "${nomeProjeto}".`,
      metadata: {
        source: 'kanban',
        projectId,
        ...(uma && tasks[0].id ? { taskId: tasks[0].id } : {}),
      },
    });
  } catch (err) {
    console.warn('Falha ao notificar responsável pela atividade:', err);
  }
}

// Chaves de deduplicação (metadata.dedupeKeys) dos avisos automáticos que a
// pessoa já recebeu. Os verificadores rodam no navegador, a cada abertura do
// app — é isto que impede o mesmo aviso de sair duas vezes.
export async function getSentDedupeKeys(
  organizationId: string,
  userId: string,
  types: NotificationType[],
  sinceDays: number
): Promise<Set<string>> {
  const desde = new Date(Date.now() - sinceDays * 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('notifications')
    .select('metadata')
    .eq('user_id', userId)
    .eq('organization_id', organizationId)
    .in('type', types)
    .gte('created_at', desde);
  if (error) throw error;

  const keys = new Set<string>();
  for (const n of (data ?? []) as Array<{ metadata?: { dedupeKeys?: unknown } | null }>) {
    const lista = n.metadata?.dedupeKeys;
    if (Array.isArray(lista)) lista.forEach(k => typeof k === 'string' && keys.add(k));
  }
  return keys;
}

// Exemplos de funções específicas para eventos comuns:

export async function notifyTaskAssigned({ organization_id, user_id, taskTitle, projectName, taskId, projectId }: {
  organization_id: string;
  user_id: string;
  taskTitle: string;
  projectName: string;
  taskId: string;
  projectId: string;
}) {
  return createNotification({
    organization_id,
    user_id,
    type: 'task_assigned',
    title: 'Nova tarefa atribuída',
    content: `Você foi atribuído à tarefa "${taskTitle}" no projeto "${projectName}".`,
    metadata: { taskId, projectId },
  });
}

export async function notifyTaskCommented({ organization_id, user_id, authorName, taskTitle, taskId, commentId }: {
  organization_id: string;
  user_id: string;
  authorName: string;
  taskTitle: string;
  taskId: string;
  commentId: string;
}) {
  return createNotification({
    organization_id,
    user_id,
    type: 'task_commented',
    title: 'Você foi mencionado em um comentário',
    content: `@${authorName} mencionou você na tarefa "${taskTitle}".`,
    metadata: { taskId, commentId },
  });
}

export async function notifyFileUploaded({ organization_id, user_id, uploaderName, fileName, projectName, fileId, projectId }: {
  organization_id: string;
  user_id: string;
  uploaderName: string;
  fileName: string;
  projectName: string;
  fileId: string;
  projectId: string;
}) {
  return createNotification({
    organization_id,
    user_id,
    type: 'file_uploaded',
    title: 'Novo arquivo enviado',
    content: `${uploaderName} enviou o arquivo "${fileName}" no projeto "${projectName}".`,
    metadata: { fileId, projectId },
  });
}

// Funções específicas para Chat
export async function notifyChatMention({ 
  organization_id, 
  user_id, 
  senderName, 
  roomName, 
  content, 
  messageId, 
  roomId 
}: {
  organization_id: string;
  user_id: string;
  senderName: string;
  roomName: string;
  content: string;
  messageId: string;
  roomId: string;
}) {
  return createNotification({
    organization_id,
    user_id,
    type: 'chat_mention',
    title: `${senderName} te mencionou`,
    content: `Em ${roomName}: ${content.substring(0, 100)}${content.length > 100 ? '...' : ''}`,
    metadata: { messageId, roomId, roomName, senderName },
  });
}

export async function notifyChatNewMessage({ 
  organization_id, 
  user_id, 
  senderName, 
  roomName, 
  content, 
  messageId, 
  roomId 
}: {
  organization_id: string;
  user_id: string;
  senderName: string;
  roomName: string;
  content: string;
  messageId: string;
  roomId: string;
}) {
  return createNotification({
    organization_id,
    user_id,
    type: 'chat_new_message',
    title: `Nova mensagem de ${senderName}`,
    content: `Em ${roomName}: ${content.substring(0, 100)}${content.length > 100 ? '...' : ''}`,
    metadata: { messageId, roomId, roomName, senderName },
  });
}

export async function notifyChatMemberAdded({ 
  organization_id, 
  user_id, 
  targetUserName, 
  roomName, 
  actorName, 
  roomId 
}: {
  organization_id: string;
  user_id: string;
  targetUserName: string;
  roomName: string;
  actorName: string;
  roomId: string;
}) {
  return createNotification({
    organization_id,
    user_id,
    type: 'chat_member_added',
    title: `${targetUserName} foi adicionado(a)`,
    content: `${actorName} adicionou ${targetUserName} à sala ${roomName}`,
    metadata: { roomId, roomName, actorName, targetUserName },
  });
}

// Classe para gerenciar notificações do chat
export class ChatNotificationService {
  private avisouFaltaDePermissao = false;

  // Estado atual, sem pedir nada. Serve pra decidir e pra diagnosticar.
  permissionStatus(): NotificationPermission | 'unsupported' {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission;
  }

  // Pedir permissão SÓ a partir de um gesto do usuário (clique). Navegador
  // descarta, e as vezes penaliza, pedido feito no carregamento da pagina: o
  // prompt nao aparece, a permissao fica em 'default' pra sempre e nenhuma
  // notificação de sistema chega — sem um único erro no console.
  async requestPermissionFromUserGesture(): Promise<boolean> {
    const status = this.permissionStatus();

    if (status === 'unsupported') {
      console.log('Este browser não suporta notificações');
      return false;
    }
    if (status === 'granted') return true;
    if (status === 'denied') {
      console.warn(
        'Notificações do chat bloqueadas para este site. Libere no cadeado da barra de endereços.'
      );
      return false;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn(`Notificações do chat não autorizadas (permissão: ${permission}).`);
    }
    return permission === 'granted';
  }

  // Mantido pelo nome antigo pra não quebrar chamadas existentes.
  async requestPermission(): Promise<boolean> {
    return this.requestPermissionFromUserGesture();
  }

  // Mostrar notificação desktop
  async showDesktopNotification(title: string, body: string, icon?: string): Promise<void> {
    const status = this.permissionStatus();

    // Nunca pedir permissão daqui: isso roda ao receber mensagem, longe de
    // qualquer clique, e o pedido seria descartado. Se falta permissão, o
    // silêncio precisa ficar explicado no console — uma vez só.
    if (status !== 'granted') {
      if (!this.avisouFaltaDePermissao) {
        this.avisouFaltaDePermissao = true;
        console.warn(
          `Notificação de desktop suprimida: permissão está "${status}". ` +
          'Abra o chat pelo botão flutuante para autorizar.'
        );
      }
      return;
    }

    try {
      const notification = new Notification(title, {
        body,
        icon: icon || '/favicon.ico',
        requireInteraction: false,
        silent: false
      });

      // Fechar automaticamente após 5 segundos
      setTimeout(() => {
        notification.close();
      }, 5000);
    } catch (err) {
      console.error('Erro ao mostrar notificação desktop:', err);
    }
  }

  // Um único contexto de áudio pra toda a vida da aba: o navegador só entrega
  // ~6 por documento, então abrir um por notificação fazia o som morrer em
  // silêncio a partir da sétima mensagem recebida.
  private audioContext: AudioContext | null = null;

  private getAudioContext(): AudioContext | null {
    if (this.audioContext) return this.audioContext;

    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return null;

    this.audioContext = new AudioContextClass();
    return this.audioContext;
  }

  // Tocar som de notificação
  playNotificationSound(): void {
    try {
      const audioContext = this.getAudioContext();
      if (!audioContext) return;

      // A política de autoplay entrega o contexto suspenso quando ele nasce
      // sem um gesto do usuário — sem retomar, nenhum som sai.
      if (audioContext.state === 'suspended') {
        audioContext.resume().catch(err => {
          console.error('Erro ao retomar contexto de áudio:', err);
        });
      }

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (err) {
      console.error('Erro ao tocar som de notificação:', err);
    }
  }

  // Processar nova mensagem para notificações  
  async processNewMessage(
    roomName: string,
    senderName: string,
    content: string,
    mentionedUserIds: string[] = []
  ): Promise<void> {
    try {
      const isMention = mentionedUserIds.length > 0;
      const title = isMention ? `${senderName} te mencionou` : `Nova mensagem de ${senderName}`;

      await this.showDesktopNotification(
        title,
        `Em ${roomName}: ${content.substring(0, 50)}${content.length > 50 ? '...' : ''}`
      );

      this.playNotificationSound();
    } catch (err) {
      console.error('Erro ao processar notificações de nova mensagem:', err);
    }
  }
}

export const chatNotificationService = new ChatNotificationService();